import {
  InfoCard,
  Link,
  Progress,
  ResponseErrorPanel,
  WarningPanel,
} from '@backstage/core-components';
import type { Entity } from '@backstage/catalog-model';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  Box,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useEffect, useState } from 'react';

type C4ciGithubRun = {
  id: number;
  name: string;
  displayTitle: string;
  status: string;
  conclusion: string;
  branch: string;
  event: string;
  htmlUrl: string;
  createdAt: string;
  updatedAt: string;
  actor: string;
  workflowId: number;
};

type C4ciGithubActionsSummary = {
  configured: boolean;
  repository: {
    slug: string;
    url: string;
  };
  workflow: {
    file: string;
    url: string;
  };
  branch: string;
  runs: C4ciGithubRun[];
  message?: string;
};

type CicdState =
  | { status: 'loading' }
  | { status: 'ready'; summary: C4ciGithubActionsSummary }
  | { status: 'error'; error: Error };

const useStyles = makeStyles(theme => ({
  summary: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  muted: {
    color: theme.palette.text.secondary,
  },
  runTable: {
    minWidth: 720,
  },
  cardBody: {
    display: 'grid',
    gap: theme.spacing(1.5),
  },
  compactRun: {
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.action.hover,
  },
  linkButton: {
    textTransform: 'none',
  },
}));

export function isC4ciGithubActionsAvailable(entity: Entity): boolean {
  return Boolean(entity.metadata.annotations?.['github.com/project-slug']);
}

function readAnnotation(entity: Entity, key: string): string {
  const value = entity.metadata.annotations?.[key];
  return typeof value === 'string' ? value : '';
}

function normalizeWorkflowName(workflowPath: string): string {
  const parts = workflowPath.split('/').filter(Boolean);
  return parts.length > 0 ? parts[parts.length - 1] : '';
}

function branchForEntity(entity: Entity): string {
  return readAnnotation(entity, 'c4ci.io/deploy-branch') || 'development';
}

function workflowForEntity(entity: Entity): string {
  return normalizeWorkflowName(
    readAnnotation(entity, 'c4ci.io/deploy-workflow'),
  );
}

function entityCicdRoute(entity: Entity): string {
  const kind = entity.kind.toLocaleLowerCase('en-US');
  const namespace = entity.metadata.namespace ?? 'default';
  return `/catalog/${namespace}/${kind}/${entity.metadata.name}/ci-cd`;
}

function formatDate(value: string): string {
  if (!value) {
    return 'not reported';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('en-GB', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function runStateLabel(run: C4ciGithubRun): string {
  if (run.status === 'completed') {
    return run.conclusion || 'completed';
  }

  return run.status || 'unknown';
}

function chipColor(run: C4ciGithubRun): 'primary' | 'secondary' | 'default' {
  const state = runStateLabel(run);
  if (state === 'success') {
    return 'primary';
  }
  if (
    ['failure', 'cancelled', 'timed_out', 'action_required'].includes(state)
  ) {
    return 'secondary';
  }
  return 'default';
}

async function fetchGithubActionsSummary(
  entity: Entity,
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
): Promise<C4ciGithubActionsSummary> {
  const slug = readAnnotation(entity, 'github.com/project-slug');
  const workflow = workflowForEntity(entity);
  const branch = branchForEntity(entity);
  const baseUrl = await discoveryApi.getBaseUrl('c4ci-release-evidence');
  const params = new URLSearchParams({ slug, branch });

  if (workflow) {
    params.set('workflow', workflow);
  }

  const response = await fetchApi.fetch(
    `${baseUrl}/v1/github-actions/runs?${params}`,
  );
  if (!response.ok) {
    throw new Error(`C4CI GitHub Actions backend returned ${response.status}`);
  }

  return (await response.json()) as C4ciGithubActionsSummary;
}

const C4ciGithubActionsStatus = ({
  summary,
}: {
  summary: C4ciGithubActionsSummary;
}) => {
  const classes = useStyles();

  return (
    <div>
      <div className={classes.summary}>
        <Chip
          color={summary.configured ? 'primary' : 'default'}
          label={
            summary.configured
              ? 'Backend GitHub token active'
              : 'Backend GitHub token missing'
          }
        />
        <Chip label="No browser GitHub OAuth" variant="outlined" />
        <Chip label={`Branch: ${summary.branch}`} variant="outlined" />
      </div>
      <Typography variant="body2">
        Repository:{' '}
        <Link to={summary.repository.url}>{summary.repository.slug}</Link>
      </Typography>
      {summary.workflow.file ? (
        <Typography variant="body2">
          Workflow:{' '}
          <Link to={summary.workflow.url}>{summary.workflow.file}</Link>
        </Typography>
      ) : null}
    </div>
  );
};

const C4ciGithubRunsTable = ({ runs }: { runs: C4ciGithubRun[] }) => {
  const classes = useStyles();

  if (runs.length === 0) {
    return (
      <Typography color="textSecondary" variant="body2">
        No workflow runs were returned for this repository and branch.
      </Typography>
    );
  }

  return (
    <Table className={classes.runTable} size="small">
      <TableHead>
        <TableRow>
          <TableCell>Run</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Actor</TableCell>
          <TableCell>Event</TableCell>
          <TableCell>Updated</TableCell>
          <TableCell>Source</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {runs.map(run => (
          <TableRow key={run.id}>
            <TableCell>
              <Typography variant="body2">
                {run.displayTitle || run.name}
              </Typography>
              <Typography color="textSecondary" variant="caption">
                {run.name}
              </Typography>
            </TableCell>
            <TableCell>
              <Chip
                color={chipColor(run)}
                label={runStateLabel(run)}
                size="small"
              />
            </TableCell>
            <TableCell>{run.actor || 'not reported'}</TableCell>
            <TableCell>{run.event || 'not reported'}</TableCell>
            <TableCell>{formatDate(run.updatedAt)}</TableCell>
            <TableCell>
              {run.htmlUrl ? (
                <Link className={classes.linkButton} to={run.htmlUrl}>
                  Open run
                </Link>
              ) : (
                'not reported'
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

const C4ciGithubActionsConfiguredWarning = ({
  summary,
}: {
  summary: C4ciGithubActionsSummary;
}) => {
  if (summary.configured) {
    return null;
  }

  return (
    <WarningPanel
      severity="info"
      title="GitHub Actions backend token is not configured"
      message={
        summary.message ??
        'The Factory backend did not receive a GitHub token, so it did not request browser OAuth. Configure the runtime secret to enable live workflow runs.'
      }
    />
  );
};

export const EntityC4ciGithubActionsContent = () => {
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [state, setState] = useState<CicdState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    fetchGithubActionsSummary(entity, discoveryApi, fetchApi)
      .then(summary => {
        if (!cancelled) {
          setState({ status: 'ready', summary });
        }
      })
      .catch(error => {
        if (!cancelled) {
          setState({
            status: 'error',
            error:
              error instanceof Error
                ? error
                : new Error('Unable to load C4CI CI/CD data'),
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [discoveryApi, entity, fetchApi]);

  if (state.status === 'loading') {
    return <Progress />;
  }

  if (state.status === 'error') {
    return (
      <ResponseErrorPanel
        title="Unable to load C4CI CI/CD"
        error={state.error}
      />
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <InfoCard title="C4CI CI/CD">
          <C4ciGithubActionsStatus summary={state.summary} />
        </InfoCard>
      </Grid>
      <Grid item xs={12}>
        <C4ciGithubActionsConfiguredWarning summary={state.summary} />
      </Grid>
      <Grid item xs={12}>
        <InfoCard title="GitHub Actions Runs">
          <C4ciGithubRunsTable runs={state.summary.runs} />
        </InfoCard>
      </Grid>
    </Grid>
  );
};

export const EntityC4ciGithubActionsCard = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [state, setState] = useState<CicdState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    fetchGithubActionsSummary(entity, discoveryApi, fetchApi)
      .then(summary => {
        if (!cancelled) {
          setState({ status: 'ready', summary });
        }
      })
      .catch(error => {
        if (!cancelled) {
          setState({
            status: 'error',
            error:
              error instanceof Error
                ? error
                : new Error('Unable to load C4CI CI/CD data'),
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [discoveryApi, entity, fetchApi]);

  return (
    <InfoCard title="C4CI CI/CD">
      {state.status === 'loading' ? <Progress /> : null}
      {state.status === 'error' ? (
        <Typography color="error" variant="body2">
          Unable to load CI/CD data from the Factory backend.
        </Typography>
      ) : null}
      {state.status === 'ready' ? (
        <Box className={classes.cardBody}>
          <C4ciGithubActionsStatus summary={state.summary} />
          {state.summary.runs[0] ? (
            <Box className={classes.compactRun}>
              <Typography variant="subtitle2">
                {state.summary.runs[0].displayTitle ||
                  state.summary.runs[0].name}
              </Typography>
              <Box mt={1}>
                <Chip
                  color={chipColor(state.summary.runs[0])}
                  label={runStateLabel(state.summary.runs[0])}
                  size="small"
                />
              </Box>
              <Typography
                className={classes.muted}
                component="p"
                variant="caption"
              >
                Updated {formatDate(state.summary.runs[0].updatedAt)}
              </Typography>
            </Box>
          ) : (
            <Typography color="textSecondary" variant="body2">
              No workflow run data returned yet.
            </Typography>
          )}
          <Link to={entityCicdRoute(entity)}>Open CI/CD</Link>
        </Box>
      ) : null}
    </InfoCard>
  );
};
